import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { resourceOrderAPI } from '../../../services/resourceOrderApi';
import { ResourceOrder } from '../../../types/resourceOrder';
import { ORDER_RESOURCES } from '../../../content/orderResources';
import Card from '../../../components/ui/Card';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { usePageTitle } from '../../../hooks/usePageTitle';

function copyCount(order: ResourceOrder): number {
  return ORDER_RESOURCES.reduce((total, resource) => total + (order.quantities[resource.id] ?? 0), 0);
}

export default function OrderList() {
  usePageTitle('Resource orders');
  const [orders, setOrders] = useState<ResourceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    resourceOrderAPI
      .fetchOrders()
      .then(setOrders)
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load orders')))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Resource orders' }]} />
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Resource orders</h1>

      {loading && <LoadingSkeleton lines={8} />}
      {error && <ErrorState error={error} title="Could not load orders" onRetry={() => window.location.reload()} />}
      {!loading && !error && orders.length === 0 && (
        <EmptyState title="No orders yet" description="Orders submitted from Order Resources will appear here." />
      )}
      {!loading && !error && orders.length > 0 && (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} hover className="!p-0">
              <Link to={`/admin/orders/${order.id}`} className="block p-6">
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {order.firstName} {order.lastName}
                  </h2>
                  <p className="text-sm text-gray-500">{format(new Date(order.createdAt), 'MMMM d, yyyy')}</p>
                </div>
                <p className="text-gray-700 mt-1">{order.organizationName}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {copyCount(order)} {copyCount(order) === 1 ? 'copy' : 'copies'}
                </p>
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
